/**
 * Cross-tab authentication synchronization utility
 * Uses BroadcastChannel API with localStorage fallback
 */

const AUTH_BC_NAME = 'lodos-auth-sync';

export interface AuthMessage {
  type: 'LOGIN' | 'LOGOUT' | 'WHO_HAS_SESSION' | 'I_HAVE_SESSION';
  token?: string;
  user?: any;
  rememberMe?: boolean;
  suppressRedirect?: boolean;
  onlyOthers?: boolean;
  redirectUrl?: string;
}

let redirectSuppressUntil: number = 0;

/**
 * Set redirect suppression for a specified duration
 */
export function setRedirectSuppress(ttlMs: number): void {
  redirectSuppressUntil = Date.now() + ttlMs;
}

/**
 * Check if redirect is currently suppressed
 */
export function isRedirectSuppressed(): boolean {
  return Date.now() < redirectSuppressUntil;
}

/**
 * Broadcast authentication message to other tabs
 */
export function broadcastAuthMessage(message: AuthMessage, options: { onlyOthers?: boolean } = {}): void {
  const payload = { ...message, ...options };

  // BroadcastChannel (primary method)
  if ('BroadcastChannel' in window) {
    try {
      const bc = new BroadcastChannel(AUTH_BC_NAME);
      bc.postMessage(payload);
      bc.close();
    } catch (error) {
      // BroadcastChannel error
    }
  }

  // localStorage fallback (for older browsers)
  try {
    localStorage.setItem(
      'auth_sync',
      JSON.stringify({ ...payload, ts: Date.now() })
    );
    // Trigger storage event manually for same-tab listeners
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'auth_sync',
      newValue: JSON.stringify({ ...payload, ts: Date.now() }),
      storageArea: localStorage
    }));
  } catch (error) {
    // localStorage sync error
  }
}

/**
 * Subscribe to authentication messages from other tabs
 */
export function subscribeAuthMessage(callback: (message: AuthMessage) => void): () => void {
  let bc: BroadcastChannel | null = null;
  let storageListener: ((ev: StorageEvent) => void) | null = null;

  // BroadcastChannel listener
  if ('BroadcastChannel' in window) {
    try {
      bc = new BroadcastChannel(AUTH_BC_NAME);
      bc.onmessage = (ev) => {
        callback(ev.data);
      };
    } catch (error) {
      // BroadcastChannel subscription error
    }
  }

  // localStorage listener (fallback)
  storageListener = (ev: StorageEvent) => {
    if (ev.key === 'auth_sync' && ev.newValue) {
      try {
        const data = JSON.parse(ev.newValue);
        callback(data);
      } catch (error) {
        // Failed to parse auth_sync message
      }
    }
  };

  window.addEventListener('storage', storageListener);

  // Return cleanup function
  return () => {
    if (bc) {
      bc.close();
    }
    if (storageListener) {
      window.removeEventListener('storage', storageListener);
    }
  };
}

